import React from 'react';
import { Box } from '@mui/material';
import logoCv from '../../../assets/logo-cv-final.png';
import { AuthErrorCode, COPY_ERROR } from '../domain/authErrors';

/**
 * SPEC-185 — Pantalla 1 del handoff `design_handoff_login_google` (opción 1a aprobada):
 * split de dos columnas, panel de marca oscuro + panel de acción claro, botón de Google
 * con sus 5 estados y los banners de error/éxito.
 *
 * Componente presentacional: el estado del flujo lo posee `LoginGate`, que es quien llama
 * a `authService`. Así los estados visuales se pueden probar sin tocar Firebase.
 */
export type AuthStatus = 'idle' | 'loading' | 'success' | 'error';

export interface LoginPageProps {
  estado: AuthStatus;
  codigoError: AuthErrorCode | null;
  onIniciarSesion: () => void;
}

// Tokens de color del handoff (sección "Design Tokens").
const ACCENT = '#a0588e';
const PANEL_DARK = '#0d0d0d';
const BORDER = '#e2e2e2';
const TEXT_PRIMARY = '#1a1a1a';

const FUENTE = 'Poppins, sans-serif';

// El handoff fija los cortes en 768px y 1024px, que no coinciden con los breakpoints por
// defecto de MUI — se usan media queries explícitas para no desviarse del diseño.
const TABLET = '@media (min-width:768px) and (max-width:1023px)';
const MOVIL = '@media (max-width:767px)';
const SIN_MOVIMIENTO = '@media (prefers-reduced-motion: reduce)';

function IconoGoogle(): React.ReactElement {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M44.5 20H24v8.5h11.7C34.2 33.3 29.6 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l6.3-6.3C34.5 5.8 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.2-4z"
      />
      <path
        fill="#34A853"
        d="M6.3 14.7l7 5.1C15 16.2 19.2 13 24 13c3 0 5.7 1.1 7.8 2.9l6.3-6.3C34.5 5.8 29.6 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"
      />
      <path
        fill="#FBBC05"
        d="M24 44c5.5 0 10.5-1.8 14.4-4.9l-6.7-5.5C29.5 35.5 26.9 36 24 36c-5.5 0-10.2-3.6-11.8-8.6l-7 5.4C8.3 39.4 15.6 44 24 44z"
      />
      <path
        fill="#EA4335"
        d="M44.5 20H24v8.5h11.7c-.8 2.3-2.2 4.3-4.1 5.7l6.7 5.5c3.9-3.6 6.2-8.9 6.2-15.7 0-1.3-.1-2.7-.2-4z"
      />
    </svg>
  );
}

function Spinner(): React.ReactElement {
  return (
    <Box
      component="span"
      role="status"
      aria-label="Verificando"
      data-testid="login-spinner"
      sx={{
        width: 20,
        height: 20,
        border: `2.5px solid ${BORDER}`,
        borderTopColor: ACCENT,
        borderRadius: '50%',
        animation: 'spin .8s linear infinite',
        '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
        [SIN_MOVIMIENTO]: { animation: 'none' },
      }}
    />
  );
}

function BannerError({ codigo }: { codigo: AuthErrorCode }): React.ReactElement {
  const { titulo, cuerpo } = COPY_ERROR[codigo];

  return (
    <Box
      role="alert"
      aria-live="assertive"
      data-testid="login-banner-error"
      data-codigo={codigo}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '11px',
        backgroundColor: '#fdf3f2',
        border: '1px solid #f5c6c2',
        borderRadius: '9px',
        padding: '13px 15px',
        marginBottom: '16px',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flex: '0 0 auto',
          width: 18,
          height: 18,
          marginTop: '1px',
          borderRadius: '50%',
          backgroundColor: '#d93025',
          color: '#ffffff',
          font: `700 12px ${FUENTE}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        !
      </Box>
      <Box>
        <Box sx={{ font: `600 12.5px ${FUENTE}`, color: '#a32620', marginBottom: '3px' }}>{titulo}</Box>
        <Box sx={{ font: `400 12px/1.55 ${FUENTE}`, color: '#8a4340' }}>{cuerpo}</Box>
      </Box>
    </Box>
  );
}

function BannerExito(): React.ReactElement {
  return (
    <Box
      role="alert"
      aria-live="assertive"
      data-testid="login-banner-exito"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        backgroundColor: '#f2f9f4',
        border: '1px solid #c3e6cd',
        borderRadius: '9px',
        padding: '13px 15px',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flex: '0 0 auto',
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: '#1e8e3e',
          color: '#ffffff',
          font: `700 11px ${FUENTE}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✓
      </Box>
      <Box sx={{ font: `500 12.5px ${FUENTE}`, color: '#1e6b33' }}>
        Acceso concedido · abriendo el dashboard
      </Box>
    </Box>
  );
}

function PanelDeMarca(): React.ReactElement {
  return (
    <Box
      data-testid="login-panel-marca"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: PANEL_DARK,
        padding: '52px 48px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        [TABLET]: { padding: '40px 32px' },
        [MOVIL]: {
          height: '140px',
          padding: 0,
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      {/* Resplandores decorativos: puramente visuales (handoff, "Accessibility"). */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          width: 520,
          height: 520,
          right: -240,
          top: -160,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(160,88,142,.45) 0%, rgba(160,88,142,.12) 45%, transparent 70%)',
          animation: 'glowpulse 7s ease-in-out infinite',
          '@keyframes glowpulse': { '0%, 100%': { opacity: 0.55 }, '50%': { opacity: 0.85 } },
          [SIN_MOVIMIENTO]: { animation: 'none' },
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          width: 360,
          height: 360,
          left: -160,
          bottom: -140,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(160,88,142,.3) 0%, transparent 70%)',
        }}
      />

      <Box
        component="img"
        src={logoCv}
        alt="CV Printing"
        sx={{
          position: 'relative',
          width: 188,
          height: 'auto',
          display: 'block',
          [MOVIL]: { width: 140 },
        }}
      />

      {/* El bloque de mensaje y el pie no se muestran en móvil (barra superior de 140px). */}
      <Box
        data-testid="login-mensaje-marca"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          [MOVIL]: { display: 'none' },
        }}
      >
        <Box
          component="h2"
          sx={{
            font: `600 32px/1.25 ${FUENTE}`,
            color: '#ffffff',
            letterSpacing: '-0.4px',
            margin: 0,
            [TABLET]: { fontSize: '26px' },
          }}
        >
          Sistema de Gestión
          <br />
          Comercial
        </Box>
        <Box
          component="p"
          sx={{
            font: `300 14.5px/1.7 ${FUENTE}`,
            color: 'rgba(255,255,255,.55)',
            maxWidth: 300,
            margin: 0,
          }}
        >
          Catálogo, proveedores, clientes, cotizaciones y contabilidad en un solo lugar.
        </Box>
        <Box
          data-testid="login-chips"
          sx={{
            display: 'flex',
            gap: '9px',
            flexWrap: 'wrap',
            marginTop: '8px',
            [TABLET]: { display: 'none' },
          }}
        >
          {['Catálogo', 'Cotización', 'Contabilidad'].map((chip) => (
            <Box
              key={chip}
              sx={{
                font: `500 11px ${FUENTE}`,
                color: 'rgba(255,255,255,.7)',
                border: '1px solid rgba(255,255,255,.17)',
                borderRadius: '999px',
                padding: '5px 12px',
              }}
            >
              {chip}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        component="p"
        sx={{
          position: 'relative',
          font: `400 11px ${FUENTE}`,
          color: 'rgba(255,255,255,.3)',
          margin: 0,
          [MOVIL]: { display: 'none' },
        }}
      >
        v1.0.0 · Powered by CV Printing S.A.S
      </Box>
    </Box>
  );
}

export function LoginPage({ estado, codigoError, onIniciarSesion }: LoginPageProps): React.ReactElement {
  const cargando = estado === 'loading';

  return (
    <Box
      data-testid="login-page"
      sx={{
        display: 'grid',
        gridTemplateColumns: '44% 1fr',
        minHeight: '100vh',
        fontFamily: FUENTE,
        [TABLET]: { gridTemplateColumns: '38% 1fr' },
        [MOVIL]: { gridTemplateColumns: '1fr', gridTemplateRows: '140px 1fr' },
      }}
    >
      <PanelDeMarca />

      <Box
        data-testid="login-panel-accion"
        sx={{
          backgroundColor: '#ffffff',
          padding: '0 72px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          [MOVIL]: { padding: '28px' },
        }}
      >
        <Box
          component="h1"
          sx={{
            font: `700 28px/1.2 ${FUENTE}`,
            color: ACCENT,
            letterSpacing: '-0.3px',
            margin: '0 0 8px',
          }}
        >
          Bienvenido
        </Box>
        <Box
          component="p"
          sx={{ font: `400 14px/1.6 ${FUENTE}`, color: '#767676', maxWidth: 320, margin: '0 0 36px' }}
        >
          Usa tu cuenta de Google corporativa para acceder al sistema.
        </Box>

        {estado === 'error' && codigoError !== null && <BannerError codigo={codigoError} />}

        {/* En éxito el banner SUSTITUYE al botón durante la transición (handoff). */}
        {estado === 'success' ? (
          <BannerExito />
        ) : (
          <Box
            component="button"
            type="button"
            data-testid="login-google-button"
            onClick={onIniciarSesion}
            disabled={cargando}
            aria-busy={cargando}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '13px',
              width: '100%',
              padding: '15px 20px',
              backgroundColor: cargando ? '#fafafa' : '#ffffff',
              border: `1.5px solid ${BORDER}`,
              borderRadius: '10px',
              font: `500 15px ${FUENTE}`,
              color: cargando ? '#8a8580' : TEXT_PRIMARY,
              boxShadow: '0 2px 8px rgba(0,0,0,.07)',
              cursor: cargando ? 'wait' : 'pointer',
              transition: 'border-color .15s, box-shadow .15s',
              '&:hover:not(:disabled)': {
                borderColor: ACCENT,
                boxShadow: '0 4px 18px rgba(160,88,142,.15)',
              },
              '&:focus-visible': {
                borderColor: ACCENT,
                boxShadow: '0 0 0 3px rgba(160,88,142,.25)',
                outline: 'none',
              },
              '&:disabled': { cursor: 'wait' },
            }}
          >
            {cargando ? <Spinner /> : <IconoGoogle />}
            {cargando ? 'Verificando…' : 'Continuar con Google'}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', margin: '26px 0' }}>
          <Box aria-hidden="true" sx={{ flex: 1, height: '1px', backgroundColor: '#ebebeb' }} />
          <Box sx={{ font: `400 12px ${FUENTE}`, color: '#bbbbbb' }}>o</Box>
          <Box aria-hidden="true" sx={{ flex: 1, height: '1px', backgroundColor: '#ebebeb' }} />
        </Box>

        <Box
          component="p"
          sx={{ font: `400 12.5px/1.7 ${FUENTE}`, color: '#a0a0a0', textAlign: 'center', margin: 0 }}
        >
          ¿Problemas para acceder?
          <br />
          <Box
            component="a"
            href="mailto:soporte@cvprinting.com.co"
            sx={{ color: ACCENT, fontWeight: 500, textDecoration: 'none' }}
          >
            Contacta a soporte
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
